import React, { useMemo } from 'react';
import { CircularProgress, FormControl, InputLabel, FormHelperText } from '@mui/material';
import ReactSelect, { MultiValue, SingleValue, type StylesConfig } from 'react-select';

import { neutral } from '_lex/_foundations/phxColors';

export type BaseSelectInputOption = {
  value: string;
  label: string;
};

export interface BaseSelectInputProps {
  options: BaseSelectInputOption[];
  value: string | string[];
  onChange: (value: string | string[]) => void;
  isMulti?: boolean;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  error?: string;
  helperText?: string;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  hideLabel?: boolean;
  isClearable?: boolean;
  isSearchable?: boolean;
  isOptionDisabled?: (option: BaseSelectInputOption) => boolean;
}

const LoadingIndicator = () => <CircularProgress size={16} sx={{ mr: 1 }} />;

const BaseSelectInput: React.FC<BaseSelectInputProps> = ({
  options,
  value,
  onChange,
  isMulti = false,
  label,
  placeholder = 'Select...',
  disabled = false,
  error,
  helperText,
  size = 'small',
  fullWidth = true,
  hideLabel = false,
  isClearable = false,
  isSearchable = true,
  isOptionDisabled,
}) => {
  const selectedValue = useMemo(() => {
    if (isMulti) {
      const arr = Array.isArray(value) ? value : [];
      return options.filter((opt) => arr.includes(opt.value));
    }
    const single = typeof value === 'string' ? value : Array.isArray(value) ? value[0] : '';
    return options.find((opt) => opt.value === single) || null;
  }, [value, options, isMulti]);

  const customStyles = useMemo<StylesConfig<BaseSelectInputOption, boolean>>(
    () => ({
      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
      menu: (base) => ({ ...base, zIndex: 9999 }),
      input: (base) => ({
        ...base,
        boxShadow: 'none',
        outline: 0,
        minHeight: size === 'small' ? 26 : 45,
      }),
      control: (base, state) => ({
        ...base,
        backgroundColor: state.isDisabled ? neutral[100] : base.backgroundColor,
        borderColor: state.isDisabled ? neutral[300] : base.borderColor,
        color: state.isDisabled ? 'rgba(0, 0, 0, 0.38)' : base.color,
        cursor: state.isDisabled ? 'not-allowed' : base.cursor,
        '&:hover': {
          borderColor: state.isDisabled ? neutral[300] : base.borderColor,
        },
      }),
      singleValue: (base, state) => ({
        ...base,
        color: state.isDisabled ? 'rgba(0, 0, 0, 0.38)' : base.color,
      }),
      placeholder: (base, state) => ({
        ...base,
        color: state.isDisabled ? 'rgba(0, 0, 0, 0.38)' : base.color,
      }),
    }),
    [size]
  );

  const handleChange = (newValue: SingleValue<BaseSelectInputOption> | MultiValue<BaseSelectInputOption>) => {
    if (isMulti && Array.isArray(newValue)) {
      onChange(newValue.map((x) => x.value));
    } else if (!isMulti && newValue && !Array.isArray(newValue) && 'value' in newValue) {
      onChange(newValue.value);
    } else if (!isMulti) {
      onChange('');
    } else {
      onChange([]);
    }
  };

  const hasLabel = !hideLabel && !!label;

  return (
    <FormControl
      fullWidth={fullWidth}
      error={!!error}
      className={error ? 'Mui-error' : ''}
      sx={hasLabel ? undefined : { paddingTop: 0, backgroundColor: 'transparent' }}
    >
      {hasLabel && <InputLabel>{label}</InputLabel>}
      <ReactSelect<BaseSelectInputOption, boolean>
        value={selectedValue}
        options={options}
        placeholder={placeholder}
        isMulti={isMulti}
        isSearchable={isSearchable}
        isClearable={isClearable}
        isDisabled={disabled}
        isOptionDisabled={isOptionDisabled}
        components={{ LoadingIndicator }}
        classNamePrefix="react-select"
        menuPortalTarget={typeof window !== 'undefined' ? document.body : undefined}
        menuPosition="fixed"
        styles={customStyles}
        onChange={handleChange}
      />
      {!!helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};

export default BaseSelectInput;
